import { toCameraLocalDateTime } from "./cameraLocalDateTime";

function readTagAsString(tag: unknown): string | undefined {
  if (tag === null || tag === undefined) return undefined;
  if (typeof tag === "string") return tag;
  if (typeof tag === "number" || typeof tag === "boolean") return String(tag);
  if (Array.isArray(tag)) {
    const first = tag.find((value) => value !== null && value !== undefined);
    return first !== undefined ? String(first) : undefined;
  }
  if (typeof tag === "object") {
    const record = tag as { description?: unknown; value?: unknown };
    if (typeof record.description === "string") return record.description;
    if (typeof record.value === "string") return record.value;
    if (typeof record.value === "number" || typeof record.value === "boolean") {
      return String(record.value);
    }
    if (Array.isArray(record.value)) {
      const first = record.value.find((value) => value !== null && value !== undefined);
      return first !== undefined ? String(first) : undefined;
    }
  }
  return undefined;
}

/**
 * Best-effort capture time from the original file. Only used to confirm name+size matches.
 * Videos are skipped here; the upload API still detects duplicates after probing capture metadata.
 */
export async function extractMediaTakenAt(file: File): Promise<string | undefined> {
  const type = (file.type || "").toLowerCase();
  const name = file.name || "";
  if (type.startsWith("video/") || /\.(mp4|mov|m4v|webm|3gp|3gpp|mkv|hevc|h265)$/i.test(name)) {
    return undefined;
  }

  try {
    const ExifReader = (await import("exifreader")).default;
    const header = file.slice(0, Math.min(file.size, 2 * 1024 * 1024));
    const metadata = (await ExifReader.load(await header.arrayBuffer(), { expanded: true })) as {
      exif?: Record<string, unknown>;
      image?: Record<string, unknown>;
      xmp?: Record<string, unknown>;
    };
    const exif = metadata.exif;
    const image = metadata.image;
    const xmp = metadata.xmp;

    const takenAt =
      readTagAsString(exif?.DateTimeOriginal) ||
      readTagAsString(exif?.CreateDate) ||
      readTagAsString(exif?.DateTimeDigitized) ||
      readTagAsString(exif?.ModifyDate) ||
      readTagAsString(image?.DateTime) ||
      readTagAsString(xmp?.CreateDate) ||
      readTagAsString(xmp?.DateCreated) ||
      readTagAsString(xmp?.ModifyDate);

    return toCameraLocalDateTime(takenAt);
  } catch {
    return undefined;
  }
}
